import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Select from 'react-select'

/* form */
import api from '../../../utils/api'
import Input from "../../../shared/components/form/input"
import Radio from "../../../shared/components/form/Radio"
import FormLayout from "../../../shared/components/FormLayout/FormLayout"
import FormSection from "../../../shared/components/FormLayout/FormSection"
import Button from "../../../shared/components/Button/Button"
import Modal from "../../../shared/modal/Modal"
import ConfirmDeleteModal from "../../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal"
import CnpjSearchTrigger from "../../../shared/components/CnpjSearchTrigger/CnpjSearchTrigger"
import { normalizeCNPJDigits, maskCNPJ } from "../../../shared/functions/cnpjFormat"
import CepSearchTrigger from "../../../shared/components/CepSearchTrigger/CepSearchTrigger"
import UfSelect from "../../../shared/components/UfSelect/UfSelect"
import CitySelect from "../../../shared/components/CitySelect/CitySelect"
import selectStyles from "../../../shared/css/SelectStyle"
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa'

/* Hooks */
import useFlashMessage from '../../../hooks/useFlashMessage'

import './FormProvider.css'
import "../../../shared/css/subItemList.css"

const EMPTY_CONTACT_DRAFT = { name: '', phone: '' }

// Unidade da regra de compra: fica por regra, não no tecido nem no fornecedor como um todo —
// o mesmo tecido físico pode ser vendido em kg por um fornecedor e em metro por outro.
const UNIT_OPTIONS = [
    { value: 'm', label: 'Metro' },
    { value: 'kg', label: 'Quilograma (Kg)' },
]

const EMPTY_CLOTH_RULE_DRAFT = { scope: 'group', cloths: [], unit: null, minPurchaseValue: '' }

function FormProvider({ handleSubmit, providerData, btnText, btCancelLink, btCancelAction, title }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()
    const [provider, setProvider] = useState(providerData || { personType: "fisica", clothPurchaseRules: [] })
    const [cloths, setCloths] = useState([])

    const [modalClothRule, setModalClothRule] = useState(false)
    const [editingClothRuleIndex, setEditingClothRuleIndex] = useState(null)
    const [clothRuleDraft, setClothRuleDraft] = useState(EMPTY_CLOTH_RULE_DRAFT)
    const [modalDeleteClothRule, setModalDeleteClothRule] = useState(false)
    const [deleteClothRuleIndex, setDeleteClothRuleIndex] = useState(null)

    const [modalContact, setModalContact] = useState(false)
    const [editingContactIndex, setEditingContactIndex] = useState(null)
    const [contactDraft, setContactDraft] = useState(EMPTY_CONTACT_DRAFT)
    const [modalDeleteContact, setModalDeleteContact] = useState(false)
    const [deleteContactIndex, setDeleteContactIndex] = useState(null)

    const contacts = provider.contacts || []

    function openAddContactModal() {
        setContactDraft(EMPTY_CONTACT_DRAFT)
        setEditingContactIndex(null)
        setModalContact(true)
    }

    function openEditContactModal(idx) {
        setContactDraft({ ...contacts[idx] })
        setEditingContactIndex(idx)
        setModalContact(true)
    }

    function closeContactModal() {
        setContactDraft(EMPTY_CONTACT_DRAFT)
        setEditingContactIndex(null)
        setModalContact(false)
    }

    function handleContactDraftChange(e) {
        setContactDraft({ ...contactDraft, [e.target.name]: e.target.value })
    }

    function saveContactDraft(e) {
        e.preventDefault()
        // Evita que o submit borbulhe (via árvore React, mesmo com o Modal via portal) e
        // acione também o onSubmit do formulário de Fornecedor.
        e.stopPropagation()

        if (!contactDraft.name) {
            setFlashMessage('Digite o nome do contato', 'error')
            return
        }
        if (!contactDraft.phone) {
            setFlashMessage('Digite o telefone do contato', 'error')
            return
        }

        const rows = editingContactIndex !== null
            ? contacts.map((c, i) => (i === editingContactIndex ? contactDraft : c))
            : [...contacts, contactDraft]
        setProvider({ ...provider, contacts: rows })
        closeContactModal()
    }

    function askRemoveContact(idx) {
        setDeleteContactIndex(idx)
        setModalDeleteContact(true)
    }

    function confirmRemoveContact() {
        setProvider({ ...provider, contacts: contacts.filter((_, i) => i !== deleteContactIndex) })
        setDeleteContactIndex(null)
        setModalDeleteContact(false)
    }

    useEffect(() => {
        api.get('/cloth/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }).then((response) => {
            setCloths(response.data.cloths || [])
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    function handleChange(e) {
        setProvider({ ...provider, [e.target.name]: e.target.value })
    }

    // CNPJ é guardado só com dígitos — a máscara é só visual, nunca digitada pelo usuário.
    // Isso evita o problema que já tivemos no bestforce: um usuário digitando com ponto/traço e
    // outro só número, deixando o cadastro inconsistente (duplicado não detectado, etc).
    function handleChangeCnpj(e) {
        const { value } = e.target
        let digits = normalizeCNPJDigits(value)
        // apagou só um caractere da máscara (ponto, barra, hífen): remove o último dígito também,
        // senão o backspace "trava" sem efeito visível
        if (digits === normalizeCNPJDigits(provider.CNPJ) && value.length < maskCNPJ(provider.CNPJ).length) {
            digits = digits.slice(0, -1)
        }
        setProvider({ ...provider, CNPJ: digits })
    }
    //Radio form start
    const handleChangeRadio = (name, value) => {
        setProvider({ ...provider, [name]: value })
    }
    //Radio form end

    function handleCnpjFound(company) {
        setProvider((prev) => ({
            ...prev,
            corporateName: company.corporateName || prev.corporateName,
            fantasyName: company.fantasyName || prev.fantasyName,
            address: company.address || prev.address,
            number: company.number || prev.number,
            complement: company.complement || prev.complement,
            district: company.district || prev.district,
            city: company.city || prev.city,
            state: company.state || prev.state,
            cep: company.cep || prev.cep,
            cityCode: company.cityCode || prev.cityCode,
            email: company.email || prev.email,
            phone: company.phone || prev.phone,
        }))
    }

    function handleCepFound(address) {
        setProvider((prev) => ({
            ...prev,
            address: address.street || prev.address,
            district: address.district || prev.district,
            city: address.city || prev.city,
            state: address.state || prev.state,
            cityCode: address.cityCode || prev.cityCode,
        }))
    }

    function handleSelectChange(selectedOption) {
        // Trocar a UF invalida a cidade escolhida antes (era de outro estado).
        if (selectedOption.selectName === 'state') {
            setProvider({ ...provider, state: selectedOption.value, city: '' })
            return
        }
        setProvider({ ...provider, [selectedOption.selectName]: selectedOption.value })
    }

    const clothPurchaseRules = provider.clothPurchaseRules || []
    // Exclui a própria regra em edição das checagens de "já usado em outra regra" — senão os
    // tecidos dela mesma ficariam bloqueados no multi-select ao reabrir pra editar.
    const hasAllRow = clothPurchaseRules.some((r, i) => r.scope === 'all' && i !== editingClothRuleIndex)
    const usedClothIds = clothPurchaseRules
        .filter((_, i) => i !== editingClothRuleIndex)
        .flatMap((r) => (r.cloths || []).map((c) => c._id))

    function openAddClothRuleModal() {
        setClothRuleDraft(EMPTY_CLOTH_RULE_DRAFT)
        setEditingClothRuleIndex(null)
        setModalClothRule(true)
    }

    function openEditClothRuleModal(idx) {
        setClothRuleDraft({ ...clothPurchaseRules[idx] })
        setEditingClothRuleIndex(idx)
        setModalClothRule(true)
    }

    function closeClothRuleModal() {
        setClothRuleDraft(EMPTY_CLOTH_RULE_DRAFT)
        setEditingClothRuleIndex(null)
        setModalClothRule(false)
    }

    function handleClothRuleDraftScope(scope) {
        setClothRuleDraft({ ...clothRuleDraft, scope, cloths: scope === 'all' ? [] : clothRuleDraft.cloths })
    }

    function handleClothRuleDraftUnit(selectedOption) {
        setClothRuleDraft({ ...clothRuleDraft, unit: selectedOption })
    }

    function handleClothRuleDraftCloths(selectedOptions) {
        setClothRuleDraft({ ...clothRuleDraft, cloths: (selectedOptions || []).map((o) => o.cloth) })
    }

    function handleClothRuleDraftValue(e) {
        setClothRuleDraft({ ...clothRuleDraft, minPurchaseValue: e.target.value })
    }

    function saveClothRuleDraft(e) {
        e.preventDefault()
        // Evita que o submit borbulhe (via árvore React, mesmo com o Modal via portal) e
        // acione também o onSubmit do formulário de Fornecedor.
        e.stopPropagation()

        if (clothRuleDraft.scope === 'group' && !(clothRuleDraft.cloths || []).length) {
            setFlashMessage('Selecione ao menos um tecido pro grupo', 'error')
            return
        }
        if (!clothRuleDraft.unit) {
            setFlashMessage('Selecione a unidade (kg/metro)', 'error')
            return
        }

        const rows = editingClothRuleIndex !== null
            ? clothPurchaseRules.map((r, i) => (i === editingClothRuleIndex ? clothRuleDraft : r))
            : [...clothPurchaseRules, clothRuleDraft]
        setProvider({ ...provider, clothPurchaseRules: rows })
        closeClothRuleModal()
    }

    function askRemoveClothRule(idx) {
        setDeleteClothRuleIndex(idx)
        setModalDeleteClothRule(true)
    }

    function confirmRemoveClothRule() {
        setProvider({ ...provider, clothPurchaseRules: clothPurchaseRules.filter((_, i) => i !== deleteClothRuleIndex) })
        setDeleteClothRuleIndex(null)
        setModalDeleteClothRule(false)
    }

    function clothRuleLabel(row) {
        return row.scope === 'all' ? 'Todos os Tecidos' : (row.cloths || []).map((c) => c.name).join(', ')
    }

    function submit(e) {
        e.preventDefault()
        // Evita que o submit borbulhe (via árvore React, mesmo com o Modal via portal) e
        // acione também o onSubmit do formulário que abriu este como "cadastro rápido".
        e.stopPropagation()
        handleSubmit(provider)
    }

    return (
        <FormLayout
            title={title}
            onSubmit={submit}
            actions={
                <>
                    <Button variant="save" type="submit">{btnText || 'Salvar'}</Button>
                    {btCancelLink
                        ? <Link className="link-default" to={btCancelLink}><Button variant="cancel">Cancelar</Button></Link>
                        : btCancelAction && <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                    }
                </>
            }
        >
            <FormSection label="Identificação">
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Fornecedor"
                            type="text"
                            name="name"
                            placeholder="digite o nome do fornecedor"
                            handleOnChange={handleChange}
                            value={provider.name || ''}
                        />
                    </div>
                </div>

                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <label className="input-label">
                            Regras de Compra por Tecido
                            <div onClick={openAddClothRuleModal} title="Adicionar regra de compra">
                                <div className="plus-sinal">
                                    <div className="plus-sinal-tip"></div>
                                    <div className="plus-sinal-middle"></div>
                                    <div className="plus-sinal-tip"></div>
                                </div>
                            </div>
                        </label>
                        <span className="fl-section-hint">Declara em qual unidade (kg/metro) esse fornecedor vende cada tecido — usado pra validar o cadastro de Produto. O mínimo de compra é opcional.</span>
                    </div>
                </div>

                <div className="fp-min-purchase-list">
                    {clothPurchaseRules.length === 0 && (
                        <span className="fp-min-purchase-empty">Nenhuma regra cadastrada ainda — clique no "+" acima pra adicionar.</span>
                    )}
                    {clothPurchaseRules.map((row, idx) => (
                        <div key={idx} className="fp-min-purchase-summary-row" title="Clique para editar" onClick={() => openEditClothRuleModal(idx)}>
                            <span className="fp-min-purchase-summary-label">{clothRuleLabel(row)}</span>
                            <span className="fp-min-purchase-summary-value">
                                {row.unit?.label}{row.minPurchaseValue ? ` — mínimo ${row.minPurchaseValue}` : ''}
                            </span>
                            <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={(e) => { e.stopPropagation(); openEditClothRuleModal(idx) }} />
                            <FaTrashAlt className="shd-icon-row-trash" title="Remover" onClick={(e) => { e.stopPropagation(); askRemoveClothRule(idx) }} />
                        </div>
                    ))}
                </div>

                <Modal
                    modalStatus={modalClothRule}
                    changeStatus={setModalClothRule}
                    w={55}
                    content={
                        <FormLayout
                            title={editingClothRuleIndex !== null ? "Editar Regra de Compra" : "Adicionar Regra de Compra"}
                            onSubmit={saveClothRuleDraft}
                            actions={
                                <>
                                    <Button variant="finish" type="submit">{editingClothRuleIndex !== null ? "Salvar" : "Adicionar"}</Button>
                                    <Button variant="cancel" type="button" onClick={closeClothRuleModal}>Cancelar</Button>
                                </>
                            }
                        >
                            <div className="fl-row">
                                <div className="fl-col-wide form-group">
                                    <Radio
                                        options={[
                                            { value: 'group', label: "Grupo de Tecidos" },
                                            ...(!hasAllRow ? [{ value: 'all', label: "Todos os Tecidos" }] : []),
                                        ]}
                                        name="scope"
                                        value={clothRuleDraft.scope}
                                        defaultValue="group"
                                        handleOnChange={(name, value) => handleClothRuleDraftScope(value)}
                                    />
                                </div>
                            </div>
                            {clothRuleDraft.scope === 'group' && (
                                <div className="fl-row">
                                    <div className="fl-col-wide form-group">
                                        <label className="input-label">Tecidos</label>
                                        <Select
                                            isMulti
                                            options={cloths
                                                .filter((c) => !usedClothIds.includes(c._id))
                                                .map((c) => ({ value: c._id, label: c.name, cloth: c }))}
                                            styles={selectStyles}
                                            onChange={handleClothRuleDraftCloths}
                                            value={(clothRuleDraft.cloths || []).map((c) => ({ value: c._id, label: c.name, cloth: c }))}
                                            placeholder="Selecione um ou mais tecidos..."
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="fl-row">
                                <div className="fl-col-mid form-group">
                                    <label className="input-label">Unidade</label>
                                    <Select
                                        options={UNIT_OPTIONS}
                                        styles={selectStyles}
                                        onChange={handleClothRuleDraftUnit}
                                        value={clothRuleDraft.unit || ''}
                                        placeholder="Kg/Metro..."
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="fl-col-mid form-group">
                                    <Input
                                        text="Mínimo de Compra"
                                        type="number"
                                        placeholder="opcional"
                                        handleOnChange={handleClothRuleDraftValue}
                                        value={clothRuleDraft.minPurchaseValue}
                                    />
                                </div>
                            </div>
                        </FormLayout>
                    }
                />

                <ConfirmDeleteModal
                    modalStatus={modalDeleteClothRule}
                    changeStatus={setModalDeleteClothRule}
                    onConfirm={confirmRemoveClothRule}
                    message="Deseja realmente excluir essa regra de compra?"
                    itemName={deleteClothRuleIndex !== null ? clothRuleLabel(clothPurchaseRules[deleteClothRuleIndex] || {}) : ''}
                />

                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Radio
                            options={[
                                { value: 'fisica', label: "Pessoa física" },
                                { value: 'juridica', label: "Pessoa jurídica" }
                            ]}
                            name={"personType"}
                            value={provider.personType}
                            defaultValue="fisica"
                            handleOnChange={handleChangeRadio}
                        />
                    </div>
                </div>
            </FormSection>

            {provider.personType === "juridica" &&
                <FormSection label="Pessoa Jurídica">
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <Input
                                text="Razão Social"
                                type="text"
                                name="corporateName"
                                placeholder="digite a razão social"
                                handleOnChange={handleChange}
                                value={provider.corporateName || ''}
                            />
                        </div>
                        <div className="fl-col-mid form-group">
                            <Input
                                text="Nome Fantasia"
                                type="text"
                                name="fantasyName"
                                placeholder=""
                                handleOnChange={handleChange}
                                value={provider.fantasyName || ''}
                            />
                        </div>
                        <div className="fl-col-narrow form-group">
                            <label className="input-label">CNPJ</label>
                            <CnpjSearchTrigger name="CNPJ" value={maskCNPJ(provider.CNPJ)} handleOnChange={handleChangeCnpj} onFound={handleCnpjFound} />
                        </div>
                    </div>
                </FormSection>
            }
            {provider.personType === "fisica" &&
                <FormSection label="Pessoa Física">
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <Input
                                text="Nome Completo"
                                type="text"
                                name="completeName"
                                placeholder="digite seu nome completo"
                                handleOnChange={handleChange}
                                value={provider.completeName || ''}
                            />
                        </div>
                        <div className="fl-col-narrow form-group">
                            <Input
                                text="CPF"
                                type="number"
                                name="CPF"
                                placeholder=""
                                handleOnChange={handleChange}
                                value={provider.CPF || ''}
                            />
                        </div>
                    </div>
                </FormSection>
            }

            <FormSection label="Endereço e Contato">
                <div className="fl-row">
                    <div className="fl-col-narrow form-group">
                        <label className="input-label">CEP</label>
                        <CepSearchTrigger name="cep" value={provider.cep} handleOnChange={handleChange} onFound={handleCepFound} />
                    </div>
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Endereço"
                            type="text"
                            name="address"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={provider.address || ''}
                        />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input
                            text="Bairro"
                            type="text"
                            name="district"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={provider.district || ''}
                        />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input
                            text="Número"
                            type="number"
                            name="number"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={provider.number || ''}
                        />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input
                            text="Complemento"
                            type="text"
                            name="complement"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={provider.complement || ''}
                        />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-narrow form-group">
                        <label className="input-label">UF</label>
                        <UfSelect value={provider.state} onChange={handleSelectChange} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <label className="input-label">Cidade</label>
                        <CitySelect uf={provider.state} value={provider.city} onChange={handleSelectChange} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input
                            text="Cód. IBGE do Município"
                            type="text"
                            name="cityCode"
                            placeholder="opcional"
                            handleOnChange={handleChange}
                            value={provider.cityCode || ''}
                        />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Email"
                            type="email"
                            name="email"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={provider.email || ''}
                        />
                    </div>
                </div>

                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <label className="input-label">
                            Contatos
                            <div onClick={openAddContactModal} title="Adicionar contato">
                                <div className="plus-sinal">
                                    <div className="plus-sinal-tip"></div>
                                    <div className="plus-sinal-middle"></div>
                                    <div className="plus-sinal-tip"></div>
                                </div>
                            </div>
                        </label>
                        <span className="fl-section-hint">Nome e telefone de cada pessoa de contato desse fornecedor.</span>
                    </div>
                </div>

                <div className="sil-list">
                    {contacts.length === 0 && (
                        <span className="sil-empty">Nenhum contato cadastrado ainda — clique no "+" acima pra adicionar.</span>
                    )}
                    {contacts.map((row, idx) => (
                        <div key={idx} className="sil-row" title="Clique para editar" onClick={() => openEditContactModal(idx)}>
                            <span className="sil-label">{row.name}</span>
                            <span className="sil-value">{row.phone}</span>
                            <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={(e) => { e.stopPropagation(); openEditContactModal(idx) }} />
                            <FaTrashAlt className="shd-icon-row-trash" title="Remover" onClick={(e) => { e.stopPropagation(); askRemoveContact(idx) }} />
                        </div>
                    ))}
                </div>

                <Modal
                    modalStatus={modalContact}
                    changeStatus={setModalContact}
                    w={45}
                    content={
                        <FormLayout
                            title={editingContactIndex !== null ? "Editar Contato" : "Adicionar Contato"}
                            onSubmit={saveContactDraft}
                            actions={
                                <>
                                    <Button variant="finish" type="submit">{editingContactIndex !== null ? "Salvar" : "Adicionar"}</Button>
                                    <Button variant="cancel" type="button" onClick={closeContactModal}>Cancelar</Button>
                                </>
                            }
                        >
                            <div className="fl-row">
                                <div className="fl-col-wide form-group">
                                    <Input
                                        text="Nome"
                                        type="text"
                                        name="name"
                                        placeholder="nome do contato"
                                        handleOnChange={handleContactDraftChange}
                                        value={contactDraft.name || ''}
                                    />
                                </div>
                                <div className="fl-col-mid form-group">
                                    <Input
                                        text="Telefone"
                                        type="tel"
                                        name="phone"
                                        placeholder=""
                                        handleOnChange={handleContactDraftChange}
                                        value={contactDraft.phone || ''}
                                    />
                                </div>
                            </div>
                        </FormLayout>
                    }
                />

                <ConfirmDeleteModal
                    modalStatus={modalDeleteContact}
                    changeStatus={setModalDeleteContact}
                    onConfirm={confirmRemoveContact}
                    message="Deseja realmente excluir esse contato?"
                    itemName={deleteContactIndex !== null ? (contacts[deleteContactIndex] || {}).name : ''}
                />

                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="OBS:"
                            type="text"
                            name="obs"
                            placeholder=""
                            handleOnChange={handleChange}
                            value={provider.obs || ''}
                        />
                    </div>
                </div>
            </FormSection>
        </FormLayout>
    )
}

export default FormProvider
